// F4: dashboard buckets, computed in the browser from the local cache.
import { currentTermEnd, nextNoticeDeadline, noticeDeadlineFor, type ContractFields } from "./contract";
import { diffDays, endOfMonth } from "./dates";

type DashboardContract = ContractFields & { id: string; deletedAt?: string | null };

export interface DashboardItem<T> {
  contract: T;
  termEnd: string | null;
  deadline: string | null;
  daysToEnd: number | null;
  daysToDeadline: number | null;
}

export interface DashboardSummary<T> {
  /** Running contracts: active status and not past their (non-renewing) end date. */
  active: DashboardItem<T>[];
  expiring30: DashboardItem<T>[];
  expiring60: DashboardItem<T>[];
  expiring90: DashboardItem<T>[];
  expired: DashboardItem<T>[];
  /** Notice deadlines from today to the end of this calendar month. */
  noticeThisMonth: DashboardItem<T>[];
  /** Deadline for the current term passed, but the term hasn't ended: act now. */
  noticeMissed: DashboardItem<T>[];
  terminated: DashboardItem<T>[];
  /** Active contracts with an upcoming deadline, soonest first. */
  upcoming: DashboardItem<T>[];
}

export function summarize<T extends DashboardContract>(contracts: readonly T[], today: string): DashboardSummary<T> {
  const s: DashboardSummary<T> = {
    active: [],
    expiring30: [],
    expiring60: [],
    expiring90: [],
    expired: [],
    noticeThisMonth: [],
    noticeMissed: [],
    terminated: [],
    upcoming: [],
  };
  const monthEnd = endOfMonth(today);

  for (const c of contracts) {
    if (c.deletedAt) continue;
    const termEnd = currentTermEnd(c, today);
    const deadline = nextNoticeDeadline(c, today);
    const item: DashboardItem<T> = {
      contract: c,
      termEnd,
      deadline,
      daysToEnd: termEnd ? diffDays(today, termEnd) : null,
      daysToDeadline: deadline ? diffDays(today, deadline) : null,
    };

    if (c.status === "terminated") {
      s.terminated.push(item);
      continue;
    }
    if (termEnd && termEnd < today) {
      s.expired.push(item);
      continue;
    }
    s.active.push(item);

    if (item.daysToEnd !== null) {
      if (item.daysToEnd <= 30) s.expiring30.push(item);
      else if (item.daysToEnd <= 60) s.expiring60.push(item);
      else if (item.daysToEnd <= 90) s.expiring90.push(item);
    }

    if (termEnd) {
      const currentDeadline = noticeDeadlineFor(termEnd, c.noticePeriodDays);
      if (currentDeadline < today) s.noticeMissed.push(item);
    }
    if (deadline && deadline >= today) {
      if (deadline <= monthEnd) s.noticeThisMonth.push(item);
      s.upcoming.push(item);
    }
  }

  const byDeadline = (a: DashboardItem<T>, b: DashboardItem<T>) =>
    (a.deadline ?? "9999").localeCompare(b.deadline ?? "9999");
  const byEnd = (a: DashboardItem<T>, b: DashboardItem<T>) => (a.termEnd ?? "9999").localeCompare(b.termEnd ?? "9999");
  s.upcoming.sort(byDeadline);
  s.noticeThisMonth.sort(byDeadline);
  s.noticeMissed.sort(byEnd);
  s.expiring30.sort(byEnd);
  s.expiring60.sort(byEnd);
  s.expiring90.sort(byEnd);
  s.expired.sort((a, b) => byEnd(b, a));
  return s;
}

/** "in 3 days", "today", "2 days ago". */
export function relativeDays(days: number | null): string {
  if (days === null) return "";
  if (days === 0) return "today";
  if (days === 1) return "tomorrow";
  if (days === -1) return "yesterday";
  return days > 0 ? `in ${days} days` : `${-days} days ago`;
}
